/**
 * AiSummaryPanel
 *
 * Renders the AI-generated financial analysis with:
 *   - Numbered section headers (FinminOS brand style)
 *   - Full markdown rendering via MarkdownRenderer for each section body
 *     (handles bold, bullet lists, tables, inline code, etc.)
 *
 * Used in:
 *   DocumentUpload    -> compact={true}  (sidebar)
 *   ProviderWorkspace -> compact={false} (summary tab, full width)
 */
import { MarkdownRenderer } from '@finmin/ui';
import type { AiSection } from '../utils';
import { PALETTE_FOREST } from '../tokens';

interface Props {
  sections: AiSection[];
  /** Compact mode: smaller font + tighter spacing for sidebars */
  compact?: boolean;
}

export function AiSummaryPanel({ sections, compact = false }: Props) {
  if (sections.length === 0) return null;

  return (
    <div className={compact ? 'space-y-4' : 'space-y-6'}>
      {sections.map((sec, i) => (
        <div key={i}>
          {/* Numbered badge + section title — omitted for untitled sections
              (e.g. a full Markdown document passed through whole), which carry
              their own headings. */}
          {sec.title && (
            <div className={`flex items-start gap-2 ${compact ? 'mb-1' : 'mb-2'}`}>
              <span
                className={`font-black shrink-0 tabular-nums ${compact ? 'text-sm' : 'text-xl'}`}
                style={{ color: PALETTE_FOREST }}
              >
                {String(i + 1).padStart(2, '0')}.
              </span>
              <h4
                className={`font-semibold leading-snug ${compact ? 'text-xs' : 'text-sm'} text-foreground`}
              >
                {sec.title}
              </h4>
            </div>
          )}

          {/* Section body rendered as markdown */}
          <div className={sec.title ? (compact ? 'pl-6' : 'pl-7') : ''}>
            <MarkdownRenderer compact={compact}>
              {sec.body}
            </MarkdownRenderer>
          </div>
        </div>
      ))}
    </div>
  );
}
